import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { portalService } from '../../services/portalService';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiBuilding, FiBarChart3, FiUsers, FiActivity, FiShield, FiTrendingUp, FiSettings } = FiIcons;

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    companies: 0,
    assets: 0,
    users: 0,
    recentClicks: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // In a real app, you'd have a dedicated endpoint for admin stats
      const [companies, auditLogs] = await Promise.all([
        portalService.getUserCompanies(),
        portalService.getAuditLogs()
      ]);

      setStats({
        companies: companies.length,
        assets: companies.reduce((sum, company) => sum + company.assetsCount, 0),
        users: 4, // Mock data
        recentClicks: auditLogs.length
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: 'Manage Companies',
      description: 'Add, edit, or remove companies',
      href: '/admin/companies',
      icon: FiBuilding,
      color: 'bg-blue-500'
    },
    {
      title: 'Manage Assets',
      description: 'Configure dashboards and reports',
      href: '/admin/assets',
      icon: FiBarChart3,
      color: 'bg-green-500'
    },
    {
      title: 'User Management',
      description: 'Manage user roles and permissions',
      href: '/admin/users',
      icon: FiUsers,
      color: 'bg-purple-500'
    },
    {
      title: 'Audit Logs',
      description: 'View access logs and activity',
      href: '/admin/audit',
      icon: FiActivity,
      color: 'bg-orange-500'
    },
    {
      title: 'Link Health',
      description: 'Monitor asset availability',
      href: '/admin/health',
      icon: FiShield,
      color: 'bg-red-500'
    },
    {
      title: 'Workspace Settings',
      description: 'Customize portal appearance',
      href: '/admin/settings',
      icon: FiSettings,
      color: 'bg-indigo-500'
    }
  ];

  const statCards = [
    {
      title: 'Companies',
      value: stats.companies,
      icon: FiBuilding,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Assets',
      value: stats.assets,
      icon: FiBarChart3,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Users',
      value: stats.users,
      icon: FiUsers,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      title: 'Recent Clicks',
      value: stats.recentClicks,
      icon: FiTrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
          <SafeIcon icon={FiShield} className="h-8 w-8 text-primary-600" />
          <span>Admin Dashboard</span>
        </h1>
        <p className="mt-2 text-gray-600">
          Manage companies, assets, users, and monitor system health.
        </p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-3xl font-bold text-gray-900">
                  {loading ? '...' : stat.value}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <SafeIcon icon={stat.icon} className={`h-6 w-6 ${stat.color}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quickActions.map((action, index) => (
            <motion.div
              key={action.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
            >
              <Link
                to={action.href}
                className="block bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 card-hover p-6"
              >
                <div className="flex items-start space-x-4">
                  <div className={`p-3 rounded-lg ${action.color}`}>
                    <SafeIcon icon={action.icon} className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {action.title}
                    </h3>
                    <p className="text-sm text-gray-600">{action.description}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;